// Part search for special orders and repairs: one search box, answers from PHORA,
// PhoneLCDParts, Kracked Screens and MobileSentrix as each site replies. On a special order,
// picking a part fills in what's being ordered, the supplier and the customer's price (the
// PHORA website price). On a repair, it's the part being used: the price stays the same and
// only the part's sales tax is added.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { get, qs } from '../lib/api';
import { useDebounced } from '../lib/hooks';
import { formatCents } from '../../shared/money';
import { PRICE_SOURCE_LABELS, SUPPLIERS, looksLikePartQuery, type PartResult, type SupplierId } from '../../shared/parts';
import type { PartGroup } from '../../shared/repair-menu';
import { Icon } from '../ui/icons';
import { Cost } from '../ui/kit';

export type PickedPart = PartResult & { supplier_name: string };

/**
 * A supplier's part for a repair (see /api/repair-menu/search). `price_cents` is the part's retail
 * value: the parts amount that's taxed on the repair. It never adds to the repair's price.
 */
export interface RepairPart extends PickedPart {
  /** The repair it's for, like "Screen replacement · LCD Assembly for iPhone 13". */
  description: string;
  group: PartGroup;
  /** A repair price to suggest when the menu has none for the model. */
  suggested_cents: number | null;
  /** Our cost, only for people who may see costs. */
  cost_cents: number | null;
}

interface SupplierState<T extends PartResult = PartResult> {
  status: 'idle' | 'loading' | 'done' | 'error';
  results: T[];
  error?: string;
}

const idle = <T extends PartResult>(): Record<SupplierId, SupplierState<T>> =>
  Object.fromEntries(SUPPLIERS.map((s) => [s.id, { status: 'idle', results: [] }])) as unknown as Record<SupplierId, SupplierState<T>>;

const supplierName = (id: SupplierId) => SUPPLIERS.find((s) => s.id === id)?.name ?? id;

/** Asks every supplier at once; each one's answer shows up as soon as it arrives. */
export function usePartSearch<T extends PartResult = PartResult>(q: string, enabled: boolean, limit = 12, path = '/api/parts/search'): Record<SupplierId, SupplierState<T>> {
  const [state, setState] = useState(() => idle<T>());
  const seq = useRef(0);
  useEffect(() => {
    const query = q.trim();
    const id = ++seq.current;
    if (!enabled || query.length < 3) {
      setState(idle<T>());
      return;
    }
    setState((s) => Object.fromEntries(SUPPLIERS.map((x) => [x.id, { ...s[x.id], status: 'loading' }])) as unknown as Record<SupplierId, SupplierState<T>>);
    for (const sup of SUPPLIERS) {
      get<{ ok: boolean; results: T[]; error?: string }>(`${path}${qs({ supplier: sup.id, q: query, limit })}`)
        .then((r) => {
          if (id === seq.current) setState((s) => ({ ...s, [sup.id]: { status: r.ok ? 'done' : 'error', results: r.results ?? [], error: r.error } }));
        })
        .catch((e: Error) => {
          if (id === seq.current) setState((s) => ({ ...s, [sup.id]: { status: 'error', results: [], error: e.message } }));
        });
    }
  }, [q, enabled, limit, path]);
  return state;
}

function stockText(p: PartResult): string {
  return p.in_stock === true ? 'In stock' : p.in_stock === false ? 'Out of stock' : 'Stock unknown';
}

function PartThumb({ src }: { src: string | null }) {
  const [ok, setOk] = useState(true);
  return (
    <span className="part-thumb" aria-hidden>
      {src && ok ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setOk(false)} /> : <Icon.tag />}
    </span>
  );
}

function PartRow<T extends PickedPart>({ p, onPick, verb = 'Order', pressed, cost, disabled, priceNote }: { p: T; onPick: (p: T) => void; verb?: string; pressed?: boolean; cost?: number | null; disabled?: boolean; priceNote?: string }) {
  return (
    <div className="part-row">
      <button type="button" className="part-pick" onClick={() => onPick(p)} aria-label={`${verb} ${p.name} from ${p.supplier_name}`} aria-pressed={pressed} disabled={disabled}>
        <PartThumb src={p.image} />
        <span className="grow">
          <span className="part-name">{p.name}</span>
          <span className="part-meta">
            <span className={`stock ${p.in_stock === false ? 'out' : p.in_stock ? 'in' : ''}`}>{stockText(p)}</span>
            {p.sku ? <span>SKU {p.sku}</span> : null}
            {cost ? <Cost cents={cost} /> : null}
          </span>
        </span>
        <span className="part-price">
          <span className="strong num">{p.price_cents ? formatCents(p.price_cents) : '—'}</span>
          <span className="tiny muted">{priceNote ?? (p.price_source ? PRICE_SOURCE_LABELS[p.price_source] : 'type a price')}</span>
        </span>
      </button>
      {p.url ? (
        <a className="icon-btn part-open" href={p.url} target="_blank" rel="noreferrer noopener" title={`Open on ${p.supplier_name}’s site`} aria-label={`Open on ${p.supplier_name}’s site`}>
          <Icon.arrowRight />
        </a>
      ) : null}
    </div>
  );
}

/**
 * The answers from every supplier: "All" shows each supplier's best four, a supplier's chip shows
 * all of its parts. `head` goes beside the supplier chips; `row` draws one part.
 */
function SupplierResults<T extends PartResult>({ state, head, row }: { state: Record<SupplierId, SupplierState<T>>; head?: ReactNode; row: (p: T & { supplier_name: string }, key: string) => ReactNode }) {
  const [only, setOnly] = useState<SupplierId | 'all'>('all');
  const count = (id: SupplierId) => state[id].results.length;
  const total = SUPPLIERS.reduce((s, x) => s + count(x.id), 0);
  const loading = SUPPLIERS.some((x) => state[x.id].status === 'loading');
  const shown = only === 'all' ? SUPPLIERS : SUPPLIERS.filter((s) => s.id === only);
  return (
    <div className="part-panel" role="region" aria-label="Parts from suppliers" aria-busy={loading || undefined}>
      <div className="part-panel-head">
        <div className="chips" role="group" aria-label="Supplier">
          <button type="button" className="chip" aria-pressed={only === 'all'} onClick={() => setOnly('all')}>All{total ? ` (${total})` : ''}</button>
          {SUPPLIERS.map((s) => (
            <button key={s.id} type="button" className="chip" aria-pressed={only === s.id} onClick={() => setOnly(s.id)}>
              {s.name}{state[s.id].status === 'loading' ? ' …' : count(s.id) ? ` (${count(s.id)})` : ''}
            </button>
          ))}
        </div>
        {head}
      </div>
      {shown.map((s) => {
        const st = state[s.id];
        const list = only === 'all' ? st.results.slice(0, 4) : st.results;
        return (
          <div key={s.id} className="part-group">
            <div className="part-group-head">
              <span className="strong">{s.name}</span>
              <span className="tiny muted">{s.site}</span>
              <span className="grow" />
              {st.status === 'loading' ? <span className="mini-spin" role="status" aria-label={`Searching ${s.name}`} /> : null}
              {st.status === 'error' ? <span className="small" style={{ color: 'var(--amber)' }}>{st.error ?? `${s.name} didn’t answer.`}</span> : null}
              {st.status === 'done' && !st.results.length ? <span className="small muted">No match</span> : null}
            </div>
            {list.map((p, i) => row({ ...p, supplier_name: s.name }, `${s.id}-${p.sku ?? p.url ?? i}`))}
            {only === 'all' && st.results.length > list.length ? (
              <button type="button" className="link-btn part-more" onClick={() => setOnly(s.id)}>Show all {st.results.length} from {s.name}</button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** The search answers under "What you're ordering". */
export function PartSearchPanel({ query, onPick, onClose }: { query: string; onPick: (p: PickedPart) => void; onClose: () => void }) {
  const dq = useDebounced(query, 450);
  const state = usePartSearch(dq, dq.trim().length >= 3, 12);
  if (query.trim().length < 3) {
    return <div className="part-panel"><p className="small muted">Type the phone and the part (like “iPhone 13 screen”) or a supplier’s SKU to search PHORA, PhoneLCDParts, Kracked Screens and MobileSentrix.</p></div>;
  }
  return (
    <SupplierResults
      state={state}
      head={<button type="button" className="link-btn" onClick={onClose}>Use what I typed</button>}
      row={(p, key) => <PartRow key={key} p={p} onPick={onPick} />}
    />
  );
}

/** The key a repair line uses for a supplier's part, so tapping the part again takes it off. */
export const repairPartKey = (p: { supplier: string; sku: string | null; url: string | null; name: string }) => `p:${p.supplier}:${(p.sku || p.url || p.name).toLowerCase()}`;

const PART_WORDS: [string, string][] = [['Screen', 'screen'], ['Battery', 'battery'], ['Back glass', 'back glass'], ['Charging port', 'charging port'], ['Camera', 'camera'], ['Speaker', 'speaker']];

/**
 * The part a repair uses, from every supplier at once (PHORA, PhoneLCDParts, Kracked Screens and
 * MobileSentrix), the same search as Customer Order. It starts with the model's screens (or the
 * part asked for); the part buttons look for other parts, or type anything (a SKU too). Picking a
 * part never changes the repair's price: its value (the special-order price) is the taxed parts
 * amount, and its cost goes on the profit report.
 */
export function RepairPartsSearch({ model, chosen = [], onPick, showCost, busy, startWord = 'screen' }: { model: string; chosen?: string[]; onPick: (p: RepairPart) => void; showCost: boolean; busy?: boolean; startWord?: string }) {
  const start = `${model} ${startWord}`.trim();
  const [q, setQ] = useState(start);
  useEffect(() => setQ(start), [start]);
  const dq = useDebounced(q, 450);
  const state = usePartSearch<RepairPart>(dq, dq.trim().length >= 3, 12, '/api/repair-menu/search');
  const wordQuery = (word: string) => `${model} ${word}`.trim();
  return (
    <div className="stack-sm">
      <div className="spread">
        <div className="small strong muted" style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Part used</div>
        <span className="small muted">doesn’t change the price · only its sales tax is added</span>
      </div>
      <div className="search">
        <Icon.search />
        <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search the suppliers, like ${model || 'iPhone 13'} battery, or a SKU`} aria-label="Search the suppliers" />
      </div>
      <div className="chips" role="group" aria-label="Part">
        {PART_WORDS.map(([label, word]) => (
          <button key={label} type="button" className="chip" aria-pressed={q.trim().toLowerCase() === wordQuery(word).toLowerCase()} onClick={() => setQ(wordQuery(word))}>{label}</button>
        ))}
      </div>
      {q.trim().length < 3 ? <p className="small muted">Type the part (like “{model || 'iPhone 13'} screen”) or a supplier’s SKU.</p> : (
        <SupplierResults
          state={state}
          row={(p, key) => <PartRow key={key} p={p} verb={chosen.includes(repairPartKey(p)) ? 'Take off' : 'Use'} pressed={chosen.includes(repairPartKey(p))} cost={showCost ? p.cost_cents : null} disabled={busy} onPick={onPick} priceNote="part value · taxed" />}
        />
      )}
    </div>
  );
}

/**
 * "Order a part" answers at the bottom of the top search bar. Only searches that are about a
 * part go to the suppliers by themselves; anything else (a name, a phone number) waits for a tap,
 * so customer details never leave STORE OS.
 */
export function SupplierPartHits({ q, onPick }: { q: string; onPick: (p: PickedPart) => void }) {
  const dq = useDebounced(q, 550);
  const [asked, setAsked] = useState('');
  const query = dq.trim();
  const on = query.length >= 3 && (looksLikePartQuery(query) || asked === query);
  const state = usePartSearch(dq, on, 3);
  if (query.length < 3) return null;
  if (!on) {
    return (
      <div className="search-group">
        <button type="button" className="search-hit" onClick={() => setAsked(query)}>
          <span className="part-thumb" aria-hidden><Icon.truck /></span>
          <span className="grow"><span className="t" style={{ display: 'block' }}>Search the suppliers for “{query}”</span><span className="s" style={{ display: 'block' }}>PHORA, PhoneLCDParts, Kracked Screens and MobileSentrix</span></span>
        </button>
      </div>
    );
  }
  const hits = SUPPLIERS.flatMap((s) => state[s.id].results.slice(0, 2).map((p) => ({ ...p, supplier_name: s.name })));
  const loading = SUPPLIERS.some((s) => state[s.id].status === 'loading');
  return (
    <div className="search-group" aria-label="Order a part from a supplier">
      <div className="search-group-head">Order a part {loading ? <span className="mini-spin" aria-hidden /> : null}</div>
      {hits.map((p, i) => (
        <button key={`${p.supplier}-${p.sku ?? p.url ?? i}`} type="button" className="search-hit" onClick={() => onPick(p)}>
          <PartThumb src={p.image} />
          <span className="grow">
            <span className="t" style={{ display: 'block' }}>{p.name}</span>
            <span className="s" style={{ display: 'block' }}>{supplierName(p.supplier)}{p.sku ? ` · SKU ${p.sku}` : ''} · {stockText(p)}</span>
          </span>
          {p.price_cents ? <span className="badge">{formatCents(p.price_cents)}</span> : null}
        </button>
      ))}
      {!loading && !hits.length ? <div className="small muted" style={{ padding: '6px 12px' }}>No parts found at the suppliers.</div> : null}
    </div>
  );
}
